import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { checkStatus } from '../utilities/fetch';
import { editableTagsPath, taggingsPath } from '../utilities/api_relative_paths';
import { createQueryString } from '../utilities/utilities';
import { Typeahead } from 'react-bootstrap-typeahead-for-bs3';
import { RECRUITER_SEARCH_PATH } from '../utilities/devex_paths';
import 'react-bootstrap-typeahead-for-bs3/css/Typeahead.css';

const AddToFolderTool = ({
  consultantId,
  currentNumberOfTags,
  setCurrentNumberOfTags,
  consultantInFolderTags,
  setConsultantInFolderTags,
  setAlertData,
}) => {
  const [targetFolder, setTargetFolder] = useState([]);
  const [autocompleteEntries, setAutocompleteEntries] = useState([]);
  const [isConsultantInFavoritesFolder, setIsConsultantInFavoritesFolder] = useState(false);

  /**
   * Perform request to fetch all current user's folders. Will then be used as autocomplete entries.
   *
   * @returns {undefined}
   */
  const fetchAutocompleteEntries = () => {
    fetch(`${editableTagsPath}${createQueryString()}`)
      .then(checkStatus)
      .then((response) => response.json())
      .then((parsedData) => setAutocompleteEntries(parsedData.data));
  };

  useEffect(() => {
    fetchAutocompleteEntries();
    const isFavorite = consultantInFolderTags.find((tag) => tag.tag.name === 'Favorites');
    setIsConsultantInFavoritesFolder(isFavorite);
  }, []);

  /**
   * Add consultant to user's `Favorites` folder.
   *
   * @returns {null|undefined}
   */
  const addConsultantToFavoritesFolder = () => {
    if (isConsultantInFavoritesFolder) return null;
    const params = {
      taggings: [
        {
          name: 'Favorites',
          taggable_id: consultantId,
          taggable_type: 'Consultant',
        },
      ],
    };

    fetch(`${taggingsPath}${createQueryString(params)}`, { method: 'POST' })
      .then(checkStatus)
      .then((response) => response.json())
      .then((parsedData) => {
        setCurrentNumberOfTags(currentNumberOfTags + 1);
        setConsultantInFolderTags(consultantInFolderTags.concat(parsedData));
        setIsConsultantInFavoritesFolder(true);
        setAlertData({ type: 'success', message: 'Consultant added to Favorites folder' });
      });
  };

  /**
   * Saves user in an existing or new folder. Note that user needs to select valid target folder in
   * the Autocomplete input (either existing or new) to save the consultant. Also, note that, to
   * add a consultant to an existing folder, we just need the name of the existing folder.
   *
   * @returns {undefined}
   */
  const addToFolder = () => {
    const params = {
      taggings: [
        {
          name: targetFolder[0].name,
          taggable_id: consultantId,
          taggable_type: 'Consultant',
        },
      ],
    };

    fetch(`${taggingsPath}${createQueryString(params)}`, { method: 'POST' })
      .then(checkStatus)
      .then((response) => response.json())
      .then((parsedData) => {
        setTargetFolder([]);
        if (parsedData[0].tag.name === 'Favorites') {
          setIsConsultantInFavoritesFolder(true);
        }
        setCurrentNumberOfTags(currentNumberOfTags + 1);
        setConsultantInFolderTags(consultantInFolderTags.concat(parsedData));
        setAlertData({ type: 'success', message: 'Tag successfully added' });
      });
  };

  /**
   * Remove existing tag
   *
   * @param {object} existingTag - Tag as object
   * @param {number} existingTag.id - ID of the tag
   *
   * @returns {undefined}
   */
  const removeExistingTag = (existingTag) => {
    const queryStringParams = createQueryString({ taggings: [existingTag.id] });
    fetch(`${taggingsPath}/${queryStringParams}`, { method: 'DELETE' })
      .then(checkStatus)
      .then((response) => response.json())
      .then(() => {
        if (existingTag.tag.name === 'Favorites') {
          setIsConsultantInFavoritesFolder(false);
        }
        setConsultantInFolderTags(
          consultantInFolderTags.filter((tag) => tag.id !== existingTag.id)
        );
        setCurrentNumberOfTags(currentNumberOfTags - 1);
      });
  };

  return (
    <div className='react-add-folder-tool'>
      <div className='select-folder margin-top-large'>
        <div className='row'>
          <div className='col-sm-8 margin-bottom-small'>
            <div className='input-group'>
              <Typeahead
                id='add-to-folder-typeahead'
                allowNew={true}
                newSelectionPrefix='New folder: '
                labelKey='name'
                onChange={(selected) => setTargetFolder(selected)}
                options={autocompleteEntries}
                placeholder='Add to folder'
                selected={targetFolder}
              />
              <span className='input-group-btn'>
                <button
                  onClick={addToFolder}
                  className='btn btn-line-primary h-padding-large'
                  disabled={!targetFolder.length}
                >
                  Add
                </button>
              </span>
            </div>
          </div>
          <div className='col-sm-4 no-padding-left'>
            <a
              className='btn btn-line-default'
              disabled={isConsultantInFavoritesFolder}
              onClick={addConsultantToFavoritesFolder}
            >
              <i className='fal icon-star'></i>
              <span className='small'>
                {isConsultantInFavoritesFolder ? 'Added to Favorites' : 'Add to Favorites'}
              </span>
            </a>
          </div>
        </div>
        <div className='list-folders margin-top-small'>
          <div className='tag-list'>
            {consultantInFolderTags.map((tag) => {
              return (
                <div className='folder-item' key={tag.id}>
                  <a
                    className='tagging-text link-blue-orange'
                    target='_blank'
                    href={`${RECRUITER_SEARCH_PATH}?filter[tag_ids][]=${tag.tag.id}`}
                    rel='noreferrer'
                  >
                    {tag.tag.name}
                  </a>
                  <a className='remove' onClick={() => removeExistingTag(tag)}></a>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

AddToFolderTool.propTypes = {
  consultantId: PropTypes.number.isRequired,
  currentNumberOfTags: PropTypes.number.isRequired,
  setCurrentNumberOfTags: PropTypes.func.isRequired,
  consultantInFolderTags: PropTypes.array.isRequired,
  setConsultantInFolderTags: PropTypes.func.isRequired,
  setAlertData: PropTypes.func.isRequired,
};

export default AddToFolderTool;
